import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import { Button, Form, Modal, FormControl, Row, Col } from "react-bootstrap";
import {
    getAllRegistry,
    getLDSUListByGatewayIds,
} from "service/gatewayService";
import { updateFormFields, getGeneralColProps } from "utils/react-bootstrap";
import { Box, styled } from "@mui/system";
import styledConst from "styles";
import { LDSUTypeDict } from "constant";
import { isHttpSuccess } from "utils/functions";
import { GatewayType, NetworkOBJ } from "constant/gateway";
import { APP_CONFIG } from "config/appConfig";
import { LDSU } from "types";

const StyledContainer = styled(Modal)`
    @media (min-width: 580px) {
        .modal-dialog {
            width: 580px;
            max-width: 580px;
        }
    }

    .modal-body {
        .filter-container {
            color: ${styledConst.thirdTextColor};

            .filterBy {
                background: #f8f8f8;
                border: 1px solid #eaeaea;
                border-radius: 4px;
                margin: 10px 0px;
                align-items: center;
                padding: 10px;

                select {
                    height: fit-content;
                    padding: 5px;
                    border: 1px solid #ced4da;
                    border-radius: 3px;
                }
            }
        }

        .scroll-multiple-list {
            max-height: 55vh;
        }

        .warning-message {
            color: ${styledConst.warningTextColor};
        }
    }
`;

type ServiceDictItem = {
    title: string;
    getListByGwId: (gatewayIds: string[]) => Promise<LDSU[]>;
    getListType: () => never[];
};

const serviceDict: { [key: string]: ServiceDictItem } = {
    [LDSUTypeDict.LDSU.label]: {
        title: `Select ${
            APP_CONFIG.APP_IS_ONPREM ? "LDSU/Auxiliary Hub" : "LDSU"
        }`,
        // allDeviceLabel: "All Sensors",
        getListByGwId: getLDSUListByGatewayIds,
        getListType: () => [],
    },
};

type SearchValues = {
    gatewayId: string;
    search: string;
};

type LDSUSelectModalProps = {
    isVisible: boolean;
    onClose: () => void;
    onFinish: (unit: LDSU) => void;
    defaultSearchValue?: SearchValues;
    unitType: string;
    gatewayId?: string;
    selectedLDSU?: LDSU | null;
};

const LDSUSelectModal = (props: LDSUSelectModalProps) => {
    const {
        isVisible,
        onClose,
        onFinish,
        defaultSearchValue = {
            gatewayId: "",
            search: "",
        },
        unitType = LDSUTypeDict.LDSU.label,
        gatewayId,
        selectedLDSU,
    } = props;

    const { getListByGwId, title } = serviceDict[unitType];
    const isOnPrem = APP_CONFIG.APP_IS_ONPREM;

    const [searchValues, setSearchValues] = useState<SearchValues>({
        ...defaultSearchValue,
        gatewayId: gatewayId ?? defaultSearchValue.gatewayId,
    });
    const [isLoading, setIsLoading] = useState(true);
    const [registries, setRegistries] = useState<any[]>([]);
    const [units, setUnits] = useState<LDSU[]>([]);
    const [filteredUnits, setFilteredUnits] = useState<LDSU[]>([]);
    const [selectedUnit, setSelectedUnit] = useState<LDSU | null>(
        selectedLDSU ?? null
    );

    const registryLookupByGatewayId = new Map(
        (registries || []).map((registry: any) => [
            registry.gateway_id,
            {
                name: registry.name,
                isNetwork: registry?.info?.OBJ === NetworkOBJ,
            },
        ])
    );

    const getRegistryOptionLabel = (name: string, targetGatewayId: string) => {
        if (!isOnPrem) {
            return name;
        }

        return `${name} (${
            registryLookupByGatewayId.get(targetGatewayId)?.isNetwork
                ? "Network"
                : "Gateway"
        })`;
    };
    const fetchAll = async () => {
        setIsLoading(true);
        try {
            const resAllRedisTry = await getAllRegistry();
            let gatewayIds = [];
            if (isHttpSuccess(resAllRedisTry.status)) {
                const { data = [] } = resAllRedisTry;
                const onboardedGateways = data.filter(
                    ({ info }: { info: any }) => info
                );
                gatewayIds = onboardedGateways.map(
                    ({ gateway_id }: any) => gateway_id
                );
                setRegistries(onboardedGateways);
            }
            // If gatewayId prop is provided, only fetch LDSUs for that gateway
            const targetGatewayIds = gatewayId ? [gatewayId] : gatewayIds;
            const _units = await getListByGwId(targetGatewayIds);
            setUnits(_units);
            setFilteredUnits(_units);
        } catch {
            // It may need to show error to user if needed
            setUnits([]);
            setFilteredUnits([]);
        } finally {
            setIsLoading(false);
        }
    };
    //
    useEffect(() => {
        if (isVisible) {
            fetchAll();
            // Reset searchValues when modal opens
            setSearchValues({
                ...defaultSearchValue,
                gatewayId: gatewayId ?? defaultSearchValue.gatewayId,
            });
            setSelectedUnit(selectedLDSU ?? null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isVisible, gatewayId, selectedLDSU]);

    useEffect(() => {
        // Use gatewayId prop if provided, otherwise use searchValues.gatewayId
        const filterGatewayId = gatewayId ?? searchValues.gatewayId ?? "";
        const { search = "" } = searchValues;
        const nextFilteredUnits = [...(units ?? [])]
            .filter(
                (unit) =>
                    filterGatewayId === "" || filterGatewayId === unit.gatewayId
            )
            .filter(
                (unit) =>
                    search === "" ||
                    unit.name.toLowerCase().includes(search.toLowerCase())
            );

        setFilteredUnits(nextFilteredUnits);
    }, [searchValues, units, gatewayId]);

    const selectSensor = (unit: LDSU) => {
        setSelectedUnit(unit);
    };

    const renderLDSUList = () => {
        if (isLoading) {
            return <div className="warning-message">loading...</div>;
        }

        if (units.length === 0) {
            return (
                <div className="error-message">
                    You don't have any device connected yet. Please ensure your
                    gateway is onboarded.
                </div>
            );
        }

        return (
            <div className="scroll-multiple-list float-left">
                {filteredUnits.map((sensor, i: number) => {
                    const { ldsu_uuid, bus, gatewayType } = sensor;
                    const optionId = `${bus}_${ldsu_uuid}`;
                    const busName =
                        gatewayType === GatewayType.LoRaWAN
                            ? "LoRaWAN"
                            : `LDSBUS ${bus}`;
                    const isNetwork =
                        registryLookupByGatewayId.get(sensor.gatewayId)
                            ?.isNetwork || false;

                    return (
                        <div key={`${i}_${optionId}`} className="modal-option">
                            <Form.Check
                                name="selected-sensor"
                                value={optionId}
                                checked={
                                    !!(
                                        selectedUnit &&
                                        optionId ===
                                            `${selectedUnit.bus}_${selectedUnit.ldsu_uuid}`
                                    )
                                }
                                custom
                                type="radio"
                                id={optionId}
                                onChange={(e) => {
                                    selectSensor(sensor);
                                }}
                                label={
                                    <Box
                                        onClick={() => {
                                            selectSensor(sensor);
                                        }}
                                        onKeyDown={() => {
                                            selectSensor(sensor);
                                        }}
                                    >
                                        <h4 style={{ whiteSpace: "pre-wrap" }}>
                                            {sensor.name}
                                        </h4>
                                        <p>
                                            {isNetwork
                                                ? registryLookupByGatewayId.get(
                                                      sensor.gatewayId
                                                  )?.name
                                                : `${sensor.gatewayName} - ${busName}`}
                                        </p>
                                        <p>
                                            {isNetwork
                                                ? "Auxiliary Hub UUID"
                                                : "LDSU UUID"}
                                            : {sensor.ldsu_uuid}
                                        </p>
                                    </Box>
                                }
                                className="float-left"
                            />
                        </div>
                    );
                })}
            </div>
        );
    };

    return (
        <StyledContainer
            show={isVisible}
            onHide={onClose}
            animation={false}
            centered
        >
            <Modal.Header>
                <Modal.Title>{title}</Modal.Title>
                <Button variant="" className="close-button" onClick={onClose}>
                    <span className="material-icons">close</span>
                </Button>
            </Modal.Header>
            <Modal.Body>
                <Form className="filter-container">
                    <Row className="filterBy">
                        <Col {...getGeneralColProps(4)}>Filter By:</Col>
                        <Col {...getGeneralColProps(8)}>
                            <FormControl
                                as="select"
                                custom
                                aria-label="org-name"
                                name="gatewayId"
                                disabled={isLoading || !!gatewayId}
                                // placeholder={allDeviceLabel}
                                value={searchValues.gatewayId}
                                onChange={(e: any) =>
                                    updateFormFields(
                                        e,
                                        searchValues,
                                        setSearchValues
                                    )
                                }
                            >
                                <option value="">
                                    {isOnPrem
                                        ? "All Gateways/Networks"
                                        : "All Gateways"}
                                </option>

                                {(registries || []).map(
                                    ({ name, gateway_id: gatewayId }: any) => {
                                        return (
                                            <option
                                                key={gatewayId}
                                                value={gatewayId}
                                            >
                                                {getRegistryOptionLabel(
                                                    name,
                                                    gatewayId
                                                )}
                                            </option>
                                        );
                                    }
                                )}
                            </FormControl>
                        </Col>
                    </Row>
                    <Row>
                        <FormControl
                            type="text"
                            name="search"
                            placeholder="Search"
                            className="mb-4"
                            value={searchValues.search || ""}
                            onChange={(e) =>
                                updateFormFields(
                                    e,
                                    searchValues,
                                    setSearchValues
                                )
                            }
                        />
                    </Row>
                </Form>

                {renderLDSUList()}
            </Modal.Body>
            {selectedUnit !== null && (
                <Modal.Footer>
                    <Button
                        variant="primary"
                        onClick={() => onFinish(selectedUnit)}
                    >
                        ADD
                    </Button>
                </Modal.Footer>
            )}
        </StyledContainer>
    );
};

LDSUSelectModal.propTypes = {
    isVisible: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    defaultSearchValue: PropTypes.object,
};

export default LDSUSelectModal;
